import React, { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Trash2, Loader2, Save, X, FileQuestion, Lock, Unlock, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { catalogueErrorMessage } from "@/lib/catalogueErrors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose (not "@/..."): these modules must RESOLVE under Jest, where the
// "@/" alias is not mapped, and the course suites mock them by these very paths.
import useTenantCurrency from "../../lib/useTenantCurrency";
import TaxCategoryField from "../TaxCategoryField";
import useTaxProfile from "../../hooks/useTaxProfile";
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import QuizQuestionsEditor from "../../components/learn/QuizQuestionsEditor";
// crm-module BEGIN C3.02/C3.04 (lane F4)
import PromoteButton from "../crm/promote/PromoteButton";
// crm-module END

// learn_telehealth_routes.py's CourseIn requires `title`. Keeps the pre-existing
// `learn.titleRequired` key instead of lib/validation.js's generic "validation.required":
// a form-specific message stays owned by the form, same convention as ServicesAdmin.jsx's
// price-ceiling key.
//
// SHEET-225: a quiz's title is REQUIRED (the course player names a quiz by its title
// alone; it takes no lesson number) and the server refuses a blank one
// (`Lesson._quiz_requires_title`). Checked here first so the practitioner is pointed at
// the quiz; the issue is keyed `lessons` and each blank quiz highlights itself.
//
// BUG-181: a PUBLISHED course needs at least one lesson (`modules/course_rules`; the server
// refuses the save with COURSE_HAS_NO_LESSONS). The issue is keyed `published`, under the
// checkbox. The same rule disables the checkbox and the last lesson's remove button below.
// It counts the lessons the save SENDS, not `lib/courseRules`' stored-row predicate, on
// purpose: the server judges the payload the same way and mints an id for every lesson
// that arrives without one, so a legacy id-less lesson becomes a real lesson by this save.
const COURSE_SCHEMA = z.object({
  title: z.string().trim().min(1, "learn.titleRequired"),
  published: z.any(),
  lessons: z.array(z.any()).superRefine((lessons, ctx) => {
    lessons.forEach((l, i) => {
      if (isUntitledQuiz(l)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, "title"], message: "learn.quizTitleRequired" });
    });
  }),
}).superRefine((course, ctx) => {
  if (course.published && course.lessons.length === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["published"], message: "learn.publishNeedsLesson" });
  }
});

function isUntitledQuiz(lesson) {
  return lesson?.lesson_type === "quiz" && !String(lesson?.title ?? "").trim();
}

/** Dollars (string, as typed) -> integer cents. */
const toCents = (dollars) => {
  if (dollars === "" || dollars === null || dollars === undefined) return 0;
  const n = Number(dollars);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
};
/** Integer cents -> dollars as a 2-decimal string, for the price input. */
const fromCents = (cents) => {
  const n = Number(cents || 0) / 100;
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

/** The editor's working copy of a stored course, or of a new one when `course` is null. */
function toEditing(course) {
  if (!course) {
    return {
      title: "", description: "", cover_url: "",
      _price_dollars: "0.00",
      published: false, lessons: [],
      tax_category: "standard", exemption_citation: "", sac_code: "",
    };
  }
  return {
    ...course,
    _price_dollars: fromCents(course.price_cents || 0),
    tax_category: course.tax_category || "standard",
    exemption_citation: course.exemption_citation || "",
    sac_code: course.sac_code || "",
    lessons: (course.lessons || []).map((l) => ({ ...l, lesson_type: l.lesson_type || "content" })),
  };
}

/**
 * The course editor: a course's details, price, tax category, publication and its
 * lessons (content or quiz). It owns its editing state and saves on its own:
 * `POST /learn/courses` for a new course, `PUT /learn/courses/{id}` for a stored one.
 *
 * The Courses page (admin/LearnAdmin.jsx) opens it, and gets the course AS THE SERVER
 * STORED IT through `onSaved`, so it never rebuilds it from the form. (Pathways choose
 * from existing courses only and never create one, contract §11.)
 *
 * Mount it with a `key` per course: the working copy is taken from `initial` once.
 *
 * @param {object}   props
 * @param {object|null} props.initial          the stored course, or null for a new one
 * @param {(course: object) => void} props.onSaved   the server's course after a 200
 * @param {() => void} props.onCancel
 */
export default function CourseEditor({ initial, onSaved, onCancel }) {
  const t = useT("admin");
  const tc = useT("common");
  const tenantCurrency = useTenantCurrency();
  const taxProfile = useTaxProfile();
  const [editing, setEditing] = useState(() => toEditing(initial));
  const [busy, setBusy] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const save = async () => {
    if (validate(COURSE_SCHEMA, editing)) { toast.error(tc("validation.fixHighlighted")); return; }
    setBusy(true);
    try {
      const payload = {
        title: editing.title,
        description: editing.description || "",
        cover_url: editing.cover_url || "",
        price_cents: toCents(editing._price_dollars),
        // Sent only when the row already has one (a legacy course). Absent, the server
        // denominates in the platform settlement currency, the only one the money core
        // will book.
        ...(editing.currency ? { currency: editing.currency } : {}),
        published: !!editing.published,
        tax_category: editing.tax_category || "standard",
        exemption_citation: editing.exemption_citation || "",
        sac_code: editing.sac_code || "",
        lessons: (editing.lessons || []).map((l, i) => ({
          ...l,
          order: i,
          // Quiz lessons are mandatory by default if not explicitly set
          mandatory: l.lesson_type === "quiz" ? (l.mandatory ?? true) : !!l.mandatory,
          quiz_questions: l.lesson_type === "quiz" ? (l.quiz_questions || []) : [],
          pass_threshold: l.lesson_type === "quiz" ? (l.pass_threshold ?? 80) : 80,
        })),
      };
      const { data } = editing.id
        ? await api.put(`/learn/courses/${editing.id}`, payload)
        : await api.post("/learn/courses", payload);
      toast.success(t("learn.saved"));
      onSaved(data);
    } catch (e) {
      toast.error(catalogueErrorMessage(e, tc, t("learn.error")));
    } finally {
      setBusy(false);
    }
  };

  const addLesson = (lesson_type = "content") => {
    const lessons = [...(editing.lessons || []), {
      id: `tmp-${Date.now()}`,
      title: "",
      body: "",
      video_url: "",
      order: (editing.lessons || []).length,
      duration_minutes: 0,
      lesson_type,
      mandatory: lesson_type === "quiz",
      quiz_questions: lesson_type === "quiz" ? [{ id: `q-${Date.now()}`, question: "", choices: ["", ""], correct_index: 0 }] : [],
      pass_threshold: 80,
    }];
    setEditing({ ...editing, lessons });
  };

  const updateLesson = (idx, patch) => {
    const lessons = [...editing.lessons];
    lessons[idx] = { ...lessons[idx], ...patch };
    setEditing({ ...editing, lessons });
    if ("title" in patch) clearFieldError("lessons");
  };

  const lessonCount = (editing.lessons || []).length;
  // Published needs a lesson: a course with none cannot be ticked (but a legacy published
  // empty course keeps a usable checkbox, so it can be unpublished), and the only lesson of
  // a published course cannot be removed.
  const publishBlocked = lessonCount === 0 && !editing.published;
  const lastLessonLocked = lessonCount === 1 && !!editing.published;

  const removeLesson = (idx) => {
    setEditing({ ...editing, lessons: editing.lessons.filter((_, i) => i !== idx) });
  };

  // The price input is labelled with the CODE, not a symbol guessed from a table that
  // no longer exists: "$" is ambiguous across the currencies a platform may settle in,
  // and the code is what the practitioner will see on their statement.
  const currencyLabel = (editing.currency || tenantCurrency || "").toUpperCase();
  return (
    <div data-testid="learn-editor" className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-3xl text-eh-ink">{editing.id ? t("learn.editCourse") : t("learn.newCourse")}</h1>
          {/* crm-module BEGIN C3.02/C3.04 — Promote (lane F4). Hidden until the course is
              saved (crm-frontend-design.md §B.4); PromoteButton itself hides for a helper. */}
          {editing.id && <PromoteButton itemType="course" itemId={editing.id} itemName={editing.title} thumbnailUrl={editing.cover_url} />}
          {/* crm-module END */}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="learn-cancel">
            <X className="w-4 h-4 mr-1" /> {t("bookings.cancel")}
          </Button>
          <Button onClick={save} disabled={busy} data-testid="learn-save" className="bg-eh-ink text-eh-bg">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} {t("actions.save")}
          </Button>
        </div>
      </div>

      {/* Course meta */}
      <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-4">
        <div>
          <Label>{t("learn.fieldTitle")}</Label>
          <Input
            value={editing.title}
            onChange={(e) => { setEditing({ ...editing, title: e.target.value }); clearFieldError("title"); }}
            data-testid="learn-course-title"
            className={fieldErrors.title ? invalidInputClass : ""}
          />
          <FieldError message={fieldErrors.title ? t(fieldErrors.title, null, "Add a title before saving.") : ""} testId="learn-course-title-error" />
        </div>
        <div>
          <Label>{t("learn.fieldDescription")}</Label>
          <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="w-full border rounded-md p-3 text-sm" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-1">
            <Label>{t("learn.coverUrl")}</Label>
            <Input value={editing.cover_url} onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })} placeholder="https://…" />
          </div>
          <div>
            <Label>{t("learn.price")}{currencyLabel ? ` (${currencyLabel})` : ""}</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={editing._price_dollars}
              onChange={(e) => setEditing({ ...editing, _price_dollars: e.target.value })}
              placeholder={t("learn.pricePlaceholder")}
              data-testid="learn-price"
            />
            <div className="text-[10px] text-eh-muted mt-1">{t("learn.freeHint")}</div>
          </div>
          {/* STATED, not chosen: see ProductsAdmin. */}
          <div>
            <Label>{t("learn.currency")}</Label>
            <div
              data-testid="learn-currency"
              className="w-full border rounded-md h-10 px-3 text-sm bg-eh-bg-soft text-eh-muted flex items-center"
            >
              {(editing.currency || tenantCurrency || "").toUpperCase() || "—"}
            </div>
          </div>
        </div>
        <TaxCategoryField
          value={{ tax_category: editing.tax_category, exemption_citation: editing.exemption_citation, sac_code: editing.sac_code }}
          onChange={(next) => setEditing({ ...editing, ...next })}
          country={taxProfile?.country}
        />
        <div>
          <label className={`flex items-center gap-2 ${publishBlocked ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
            <input
              type="checkbox"
              checked={!!editing.published}
              disabled={publishBlocked}
              onChange={(e) => { setEditing({ ...editing, published: e.target.checked }); clearFieldError("published"); }}
              data-testid="learn-published"
            />
            <span className="text-sm">{t("learn.publishLabel")}</span>
          </label>
          {publishBlocked && (
            <div className="text-[11px] text-eh-muted mt-1" data-testid="learn-published-hint">{t("learn.publishNeedsLesson")}</div>
          )}
          <FieldError message={fieldErrors.published ? t(fieldErrors.published) : ""} testId="learn-published-error" />
        </div>
      </div>

      {/* Lessons */}
      <div className="bg-white rounded-2xl border border-eh-border-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">{t("learn.lessonsWord")} ({(editing.lessons || []).length})</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => addLesson("content")} data-testid="learn-add-lesson">
              <BookOpen className="w-3 h-3 mr-1" /> {t("learn.addLesson")}
            </Button>
            <Button size="sm" onClick={() => addLesson("quiz")} data-testid="learn-add-quiz" className="bg-amber-100 text-amber-900 hover:bg-amber-200">
              <FileQuestion className="w-3 h-3 mr-1" /> {t("learn.addQuiz")}
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          {(editing.lessons || []).map((lesson, idx) => (
            <LessonEditor
              key={lesson.id || idx}
              idx={idx}
              lesson={lesson}
              t={t}
              titleError={fieldErrors.lessons && isUntitledQuiz(lesson) ? t("learn.quizTitleRequired") : ""}
              onUpdate={(patch) => updateLesson(idx, patch)}
              onRemove={() => removeLesson(idx)}
              removeLockedReason={lastLessonLocked ? t("learn.lastLessonPublished") : ""}
            />
          ))}
          {(editing.lessons || []).length === 0 && (
            <p className="text-eh-muted text-sm text-center py-6">
              {t("learn.noLessons")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Course quiz questions keep a client-made id (the course editor's historical behaviour);
// the shared editor calls this for every question it adds.
const makeCourseQuestionId = () => `q-${Date.now()}`;

/** The editor of one lesson: content (video + body) or quiz (threshold + questions). */
/** Mirrors `learn_telehealth_routes.LESSON_DURATION_MAX` (minutes in a day). */
export const LESSON_DURATION_MAX = 1440;

/** The typed duration as whole minutes within 0..LESSON_DURATION_MAX ("" and junk read as 0). */
export function lessonMinutes(raw) {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, LESSON_DURATION_MAX);
}

function LessonEditor({ idx, lesson, t, titleError, onUpdate, onRemove, removeLockedReason }) {
  const isQuiz = lesson.lesson_type === "quiz";
  return (
    <div className={`rounded-xl p-4 border ${isQuiz ? "bg-amber-50/40 border-amber-200" : "bg-eh-cream/30 border-transparent"}`} data-testid={`learn-lesson-${idx}`}>
      <div className="flex gap-2 mb-2 items-start">
        <span className={`text-xs px-2 py-1 rounded mt-1 inline-flex items-center gap-1 ${isQuiz ? "bg-amber-200 text-amber-900" : "bg-eh-ink text-eh-bg"}`}>
          {isQuiz ? <FileQuestion className="w-3 h-3" /> : <BookOpen className="w-3 h-3" />}
          #{idx + 1} {isQuiz ? t("learn.quiz") : t("learn.lesson")}
        </span>
        <div className="flex-1">
          <Input placeholder={isQuiz ? t("learn.titlePlaceholderQuiz") : t("learn.titlePlaceholderLesson")} value={lesson.title} onChange={(e) => onUpdate({ title: e.target.value })} data-testid={`lesson-title-${idx}`} className={titleError ? invalidInputClass : ""} />
          <FieldError message={titleError} testId={`lesson-title-error-${idx}`} />
        </div>
        <button
          onClick={onRemove}
          disabled={!!removeLockedReason}
          title={removeLockedReason || undefined}
          aria-description={removeLockedReason || undefined}
          className="p-2 text-rose-500 hover:bg-rose-50 rounded disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
          data-testid={`lesson-remove-${idx}`}
        ><Trash2 className="w-4 h-4" /></button>
      </div>

      {/* Mandatory toggle */}
      <label className="flex items-center gap-2 cursor-pointer text-xs mb-3 select-none">
        <input
          type="checkbox"
          checked={!!lesson.mandatory}
          onChange={(e) => onUpdate({ mandatory: e.target.checked })}
          data-testid={`lesson-mandatory-${idx}`}
        />
        {lesson.mandatory ? <Lock className="w-3 h-3 text-amber-600" /> : <Unlock className="w-3 h-3 text-eh-muted" />}
        <span className="text-eh-muted">{isQuiz ? t("learn.mandatoryDescQuiz") : t("learn.mandatoryDescLesson")}</span>
      </label>

      {/* The author's estimate of the lesson's length: course and pathway durations (the
          pathway page's total hours and "≈ N weeks") are summed from it. */}
      <label className="flex items-center gap-2 text-xs mb-3">
        <span className="text-eh-muted">{t("learn.durationLabel")}</span>
        <input
          type="number"
          min="0"
          max={LESSON_DURATION_MAX}
          value={lesson.duration_minutes ?? 0}
          onChange={(e) => onUpdate({ duration_minutes: lessonMinutes(e.target.value) })}
          data-testid={`lesson-duration-${idx}`}
          className="w-20 border rounded h-8 px-2 text-xs"
        />
        <span className="text-eh-muted">{t("learn.minutesSuffix")}</span>
      </label>

      {!isQuiz && (
        <>
          <Input placeholder={t("learn.videoPlaceholder")} value={lesson.video_url} onChange={(e) => onUpdate({ video_url: e.target.value })} className="mb-2 text-xs" />
          <textarea placeholder={t("learn.lessonBodyPlaceholder")} value={lesson.body} onChange={(e) => onUpdate({ body: e.target.value })} rows={2} className="w-full border rounded-md p-2 text-xs" />
        </>
      )}

      {isQuiz && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-xs">
            <Label className="text-xs">{t("learn.passThreshold")}</Label>
            <input
              type="number"
              min="0" max="100"
              value={lesson.pass_threshold ?? 80}
              onChange={(e) => onUpdate({ pass_threshold: parseInt(e.target.value || "0", 10) })}
              data-testid={`quiz-threshold-${idx}`}
              className="w-16 border rounded h-8 px-2 text-xs"
            />
            <span className="text-eh-muted">%</span>
          </div>

          <QuizQuestionsEditor
            questions={lesson.quiz_questions || []}
            onChange={(quiz_questions) => onUpdate({ quiz_questions })}
            scope={idx}
            makeQuestionId={makeCourseQuestionId}
          />
        </div>
      )}
    </div>
  );
}
